import {
  Button,
  Combobox,
  LinkButton,
  Spinner,
  Tag,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { gql, useMutation } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Center, Flex } from "styled-system/jsx";
import { Xmark } from "iconoir-react";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useOrgHome } from "apps/home/hooks";
import { useOperatorTagsForSettings } from "apps/widgets/views/SettingsView/useOperatorTagsForSettings";
import {
  OrganizationByIdDocument,
  OrganizationsByUserDocument,
  useOrganizationAppSettingsWidgetsQuery,
  useOrganizationUpdateMutation,
} from "gql/generated";
import { useFeatureFlag } from "shared/contextProviders";
import { FeatureFlags } from "shared/featureFlags";
import {
  DraggableItem,
  InlineSectionTitle,
  Sortable,
} from "shared/components";
import { Link } from "react-router";
import { StickyHeader } from "../MembershipTiers/StickyHeader";
import {
  type AudienceTagFilter,
  audienceTagFiltersEqual,
  audienceTagFiltersToEventOrganizerInput,
  audienceTagFiltersToEventVenueInput,
  audienceTagFiltersToLegacyIncludeIds,
  audienceTagFiltersToWhatsNearbyInput,
  normalizeAudienceTagFilters,
} from "shared/utils/audienceTagFilters";

type AudienceItem = { _id: string; tagId: string | null };
type TagOption = { value: string; label: string };

const SEED_DEFAULT_AUDIENCES = gql`
  mutation OrganizationSeedDefaultAudiences($organizationId: ID!) {
    organizationSeedDefaultAudiences(organizationId: $organizationId) {
      _id
      metadata {
        listingAudienceTagIds
      }
    }
  }
`;

const UPDATE_LISTING_AUDIENCES = gql`
  mutation OrganizationUpdateListingAudiences(
    $organizationId: ID!
    $listingAudienceTagIds: [ID!]!
  ) {
    organizationUpdateListingAudiences(
      organizationId: $organizationId
      listingAudienceTagIds: $listingAudienceTagIds
    ) {
      _id
      metadata {
        listingAudienceTagIds
      }
    }
  }
`;

// Temporarily hidden — re-enable with CUSTOM_LISTS_ENABLED
/*
const CREATE_AUDIENCE_LISTS = gql`
  mutation ListCreateListsForAudiences($organizationId: ID!) {
    listCreateListsForAudiences(organizationId: $organizationId) {
      _id
    }
  }
`;
*/

const settingsCardProps = {
  flexDir: "column" as const,
  gap: "300" as const,
  p: "300" as const,
  bgColor: "bg.wa.lightest" as const,
  border: "1px solid" as const,
  borderColor: "border.light" as const,
  borderRadius: "200" as const,
};

function AudienceTagDropdown({
  tagOptions,
  filterPredicate,
  value,
  onValueChange,
  disabled,
  loading,
  placeholder,
}: {
  tagOptions: TagOption[];
  filterPredicate: (itemText: string, filterText: string) => boolean;
  value: string | null;
  onValueChange: (tagId: string | null) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder: string;
}) {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.DIRECTORY_SETTINGS.AUDIENCES",
  });

  const { collection, filter: setFilterText, set } = Combobox.useCollection({
    initialItems: tagOptions,
    filter: filterPredicate,
  });

  const prevKey = useRef("");
  useEffect(() => {
    const key = tagOptions.map(o => o.value).join(",");
    if (key !== prevKey.current) {
      prevKey.current = key;
      set(tagOptions);
    }
  }, [tagOptions, set]);

  if (tagOptions.length === 0) return null;

  return (
    <Combobox.Root
      value={value ? [value] : []}
      onValueChange={d => onValueChange(d.value[0] ?? null)}
      onInputValueChange={d => setFilterText(d.inputValue)}
      collection={collection}
      placeholder={placeholder}
      disabled={disabled || loading}
      style={{ flex: 1, minWidth: 0 }}
    >
      {collection.items.length > 0 ? (
        collection.items.map(opt => (
          <Combobox.Item key={opt.value} item={opt} label={opt.label} />
        ))
      ) : (
        <Text color="text.light" py="200" px="300" textStyle="body.sm">
          {t("NO_RESULTS")}
        </Text>
      )}
    </Combobox.Root>
  );
}

function AudienceTagFilterSelect({
  label,
  options,
  selectedFilters,
  onChange,
}: {
  label: string;
  options: TagOption[];
  selectedFilters: AudienceTagFilter[];
  onChange: (filters: AudienceTagFilter[]) => void;
}) {
  const selectedById = useMemo(() => {
    const map = new Map<string, AudienceTagFilter>();
    for (const filter of selectedFilters) {
      map.set(filter.tagId, filter);
    }
    return map;
  }, [selectedFilters]);

  const cycleAudience = (tagId: string) => {
    const current = selectedById.get(tagId);
    let next: AudienceTagFilter[];

    if (!current) {
      next = [...selectedFilters, { tagId, mode: "include" }];
    } else if (current.mode === "include") {
      next = selectedFilters.map(filter =>
        filter.tagId === tagId ? { ...filter, mode: "exclude" } : filter,
      );
    } else {
      next = selectedFilters.filter(filter => filter.tagId !== tagId);
    }

    onChange(next);
  };

  return (
    <Flex flexDir="column" gap="100">
      <Text visual="bodySemiBold">{label}</Text>
      {options.length === 0 ? (
        <Text visual="smallRegular" color="text.mid">
          —
        </Text>
      ) : (
        <Flex flexWrap="wrap" gap="100">
          {options.map(option => {
            const selected = selectedById.get(option.value);
            const visual = !selected
              ? "primary"
              : selected.mode === "include"
                ? "success"
                : "destructive";

            return (
              <Tag
                key={option.value}
                visual={visual}
                mode="light"
                size="sm"
                asChild
              >
                <button
                  type="button"
                  onClick={() => cycleAudience(option.value)}
                  style={{ cursor: "pointer" }}
                >
                  {option.label}
                </button>
              </Tag>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
}

export const ListingAudiences = () => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.DIRECTORY_SETTINGS.AUDIENCES",
  });
  const audiencesEnabled = useFeatureFlag(FeatureFlags.ListingAudiences);
  const { toastFactory } = useToastContext();
  const { organization, loading: orgLoading } = useOrgHome();
  const organizationId = organization?._id;

  const { tagOptions, filterPredicate, loading: tagsLoading } =
    useOperatorTagsForSettings(organizationId);

  const { data: widgetSettingsData, refetch: refetchWidgetSettings } =
    useOrganizationAppSettingsWidgetsQuery({
      variables: { id: organizationId! },
      skip: !organizationId,
    });

  const savedAudienceIds = useMemo(
    () => (organization?.metadata?.listingAudienceTagIds ?? []) as string[],
    [organization?.metadata?.listingAudienceTagIds],
  );

  const savedEventOrganizerAudienceFilters = useMemo(
    () =>
      normalizeAudienceTagFilters(
        widgetSettingsData?.organizationById?.appSettings?.widgets?.event
          ?.organizerAudienceTagFilters,
      ),
    [
      widgetSettingsData?.organizationById?.appSettings?.widgets?.event
        ?.organizerAudienceTagFilters,
    ],
  );

  const savedEventVenueAudienceFilters = useMemo(
    () =>
      normalizeAudienceTagFilters(
        widgetSettingsData?.organizationById?.appSettings?.widgets?.event
          ?.venueAudienceTagFilters,
      ),
    [
      widgetSettingsData?.organizationById?.appSettings?.widgets?.event
        ?.venueAudienceTagFilters,
    ],
  );

  const savedWhatsNearbyAudienceFilters = useMemo(
    () =>
      normalizeAudienceTagFilters(
        widgetSettingsData?.organizationById?.appSettings?.widgets?.whatsNearby
          ?.audienceTagFilters,
        widgetSettingsData?.organizationById?.appSettings?.widgets?.whatsNearby
          ?.audienceTagIds,
      ),
    [
      widgetSettingsData?.organizationById?.appSettings?.widgets?.whatsNearby
        ?.audienceTagFilters,
      widgetSettingsData?.organizationById?.appSettings?.widgets?.whatsNearby
        ?.audienceTagIds,
    ],
  );

  const audienceTagOptions = useMemo(
    () => tagOptions.filter(o => savedAudienceIds.includes(o.value)),
    [tagOptions, savedAudienceIds],
  );

  const [items, setItems] = useState<AudienceItem[]>([]);
  const [eventOrganizerAudienceFilters, setEventOrganizerAudienceFilters] =
    useState<AudienceTagFilter[]>([]);
  const [eventVenueAudienceFilters, setEventVenueAudienceFilters] = useState<
    AudienceTagFilter[]
  >([]);
  const [whatsNearbyAudienceFilters, setWhatsNearbyAudienceFilters] = useState<
    AudienceTagFilter[]
  >([]);

  useEffect(() => {
    setItems(savedAudienceIds.map(tagId => ({ _id: crypto.randomUUID(), tagId })));
  }, [savedAudienceIds.join(",")]);

  useEffect(() => {
    setEventOrganizerAudienceFilters(savedEventOrganizerAudienceFilters);
  }, [JSON.stringify(savedEventOrganizerAudienceFilters)]);

  useEffect(() => {
    setEventVenueAudienceFilters(savedEventVenueAudienceFilters);
  }, [JSON.stringify(savedEventVenueAudienceFilters)]);

  useEffect(() => {
    setWhatsNearbyAudienceFilters(savedWhatsNearbyAudienceFilters);
  }, [JSON.stringify(savedWhatsNearbyAudienceFilters)]);

  const currentAudienceIds = useMemo(
    () => items.map(i => i.tagId).filter((id): id is string => !!id),
    [items],
  );

  const isAudiencesDirty =
    JSON.stringify(currentAudienceIds) !== JSON.stringify(savedAudienceIds);
  const isWidgetsDirty =
    !audienceTagFiltersEqual(
      eventOrganizerAudienceFilters,
      savedEventOrganizerAudienceFilters,
    ) ||
    !audienceTagFiltersEqual(
      eventVenueAudienceFilters,
      savedEventVenueAudienceFilters,
    ) ||
    !audienceTagFiltersEqual(
      whatsNearbyAudienceFilters,
      savedWhatsNearbyAudienceFilters,
    );

  const refetchQueries = [
    OrganizationByIdDocument,
    OrganizationsByUserDocument,
  ];

  const [seedDefaults, { loading: seeding }] = useMutation(
    SEED_DEFAULT_AUDIENCES,
    { refetchQueries },
  );

  const [updateAudiences, { loading: savingAudiences }] = useMutation(
    UPDATE_LISTING_AUDIENCES,
    { refetchQueries },
  );

  /*
  const [createLists, { loading: creatingLists }] = useMutation(
    CREATE_AUDIENCE_LISTS,
    { refetchQueries: [ListManyDocument, ...refetchQueries] },
  );
  */

  const [updateOrganization, { loading: savingWidgets }] =
    useOrganizationUpdateMutation({
      refetchQueries,
    });

  const isDirty = isAudiencesDirty || isWidgetsDirty;
  const isSaving = savingAudiences || savingWidgets;

  const handleSeedDefaults = useCallback(async () => {
    if (!organizationId) return;
    try {
      await seedDefaults({ variables: { organizationId } });
      toastFactory.success(t("SEED_SUCCESS"));
    } catch {
      toastFactory.error(t("SEED_ERROR"));
    }
  }, [organizationId, seedDefaults, t, toastFactory]);

  const handleSave = useCallback(async () => {
    if (!organizationId || !isDirty) return;
    try {
      if (isAudiencesDirty) {
        await updateAudiences({
          variables: {
            organizationId,
            listingAudienceTagIds: currentAudienceIds,
          },
        });
      }
      if (isWidgetsDirty) {
        await updateOrganization({
          variables: {
            id: organizationId,
            record: {
              appSettings: {
                widgets: {
                  event: {
                    organizerAudienceTagFilters:
                      audienceTagFiltersToEventOrganizerInput(
                        eventOrganizerAudienceFilters,
                      ),
                    venueAudienceTagFilters: audienceTagFiltersToEventVenueInput(
                      eventVenueAudienceFilters,
                    ),
                  },
                  whatsNearby: {
                    audienceTagFilters:
                      audienceTagFiltersToWhatsNearbyInput(
                        whatsNearbyAudienceFilters,
                      ),
                    audienceTagIds:
                      audienceTagFiltersToLegacyIncludeIds(
                        whatsNearbyAudienceFilters,
                      ),
                  },
                },
              },
            },
          },
        });
        await refetchWidgetSettings();
      }
      toastFactory.success(t("SAVE_SUCCESS"));
    } catch {
      toastFactory.error(t("SAVE_ERROR"));
    }
  }, [
    organizationId,
    isDirty,
    isAudiencesDirty,
    isWidgetsDirty,
    currentAudienceIds,
    eventOrganizerAudienceFilters,
    eventVenueAudienceFilters,
    whatsNearbyAudienceFilters,
    updateAudiences,
    updateOrganization,
    refetchWidgetSettings,
    t,
    toastFactory,
  ]);

  /*
  const handleCreateLists = useCallback(async () => {
    if (!organizationId) return;
    try {
      await createLists({ variables: { organizationId } });
      toastFactory.success(t("CREATE_LISTS_SUCCESS"));
    } catch {
      toastFactory.error(t("CREATE_LISTS_ERROR"));
    }
  }, [organizationId, createLists, t, toastFactory]);
  */

  const updateItemTagId = useCallback((index: number, tagId: string | null) => {
    setItems(prev =>
      prev.map((item, i) => (i === index ? { ...item, tagId } : item)),
    );
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addAudience = useCallback(() => {
    setItems(prev => [...prev, { _id: crypto.randomUUID(), tagId: null }]);
  }, []);

  const handleSortChange = useCallback(async (newItems: Partial<AudienceItem>[]) => {
    setItems(newItems as AudienceItem[]);
  }, []);

  const getTagIdsUsedInOtherAudiences = useCallback(
    (currentIndex: number) =>
      items
        .map((item, i) => (i === currentIndex ? null : item.tagId))
        .filter((id): id is string => id != null),
    [items],
  );

  const noCustomTags = !tagsLoading && tagOptions.length === 0;

  if (!audiencesEnabled) return null;

  return (
    <Flex flexDir="column" gap="250">
      <Flex flexDir="column" gap="250" w="full" alignSelf="flex-start">
        <StickyHeader>
          <InlineSectionTitle
            title={t("TITLE")}
            dividerProps={{ display: "none" }}
          />
          <Button
            visual="accent.blue"
            type="button"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            isLoading={isSaving}
          >
            {t("SAVE")}
          </Button>
        </StickyHeader>
        <Text color="text.mid">{t("INTRO")}</Text>

        <Flex flexDir="column" gap="300">
          {orgLoading || tagsLoading ? (
            <Center w="full" py="400">
              <Spinner w="40px" h="40px" />
            </Center>
          ) : noCustomTags ? (
            <Text color="text.mid">{t("NO_CUSTOM_TAGS")}</Text>
          ) : (
            <>
              {savedAudienceIds.length === 0 && items.length === 0 && (
                <Flex
                  flexDir="column"
                  gap="200"
                  p="300"
                  bg="white"
                  borderWidth="1px"
                  borderColor="border.light"
                  borderRadius="200"
                  alignItems="flex-start"
                >
                  <Text color="text.mid">{t("SEED_DEFAULTS_EMPTY")}</Text>
                  <Button
                    visual="primary"
                    mode="light"
                    type="button"
                    onClick={handleSeedDefaults}
                    disabled={seeding}
                    isLoading={seeding}
                  >
                    {t("SEED_DEFAULTS_BUTTON")}
                  </Button>
                </Flex>
              )}
              {items.length === 0 ? (
                <LinkButton
                  alignSelf="flex-start"
                  onClick={addAudience}
                  type="button"
                  disabled={tagsLoading}
                >
                  + {t("ADD_AUDIENCE_BUTTON")}
                </LinkButton>
              ) : (
                <>
                  <Sortable
                    disabled={isSaving}
                    value={items}
                    selectorID="_id"
                    sortingStrategy={verticalListSortingStrategy}
                    sortableComponent={(item, i) => (
                      <DraggableItem>
                        <Flex
                          gap="200"
                          alignItems="center"
                          width="100%"
                          onPointerDown={e => e.stopPropagation()}
                          onMouseDown={e => e.stopPropagation()}
                          onTouchStart={e => e.stopPropagation()}
                        >
                          <Flex
                            position="relative"
                            flex={1}
                            minWidth={0}
                            alignItems="center"
                          >
                            {tagOptions.length > 0 ? (
                              <AudienceTagDropdown
                                tagOptions={tagOptions.filter(
                                  opt =>
                                    !getTagIdsUsedInOtherAudiences(i).includes(
                                      opt.value,
                                    ),
                                )}
                                filterPredicate={filterPredicate}
                                value={item.tagId ?? null}
                                onValueChange={tagId => updateItemTagId(i, tagId)}
                                disabled={isSaving}
                                loading={tagsLoading}
                                placeholder={t("SELECT_AUDIENCE_PLACEHOLDER")}
                              />
                            ) : (
                              <Flex
                                h="40px"
                                alignItems="center"
                                color="text.light"
                                fontSize="sm"
                              >
                                {tagsLoading ? "…" : t("SELECT_AUDIENCE_PLACEHOLDER")}
                              </Flex>
                            )}
                          </Flex>
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            minW="40px"
                            minH="40px"
                          >
                            <Button
                              Icon={Xmark}
                              mode="light"
                              type="button"
                              onClick={() => removeItem(i)}
                              disabled={isSaving}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                                padding: 0,
                              }}
                            />
                          </Flex>
                        </Flex>
                      </DraggableItem>
                    )}
                    onChange={handleSortChange}
                    size="compact"
                  />
                  <LinkButton
                    alignSelf="flex-start"
                    onClick={addAudience}
                    type="button"
                    disabled={isSaving || tagsLoading}
                  >
                    + {t("ADD_AUDIENCE_BUTTON")}
                  </LinkButton>
                </>
              )}
            </>
          )}
        </Flex>
      </Flex>

      {/* Custom Lists temporarily hidden — set CUSTOM_LISTS_ENABLED to true and uncomment related code */}

      {savedAudienceIds.length > 0 && (
        <Flex {...settingsCardProps}>
          <Text visual="bodySemiBold">{t("WIDGETS_TITLE")}</Text>
          <Text color="text.mid">{t("WIDGETS_DESCRIPTION")}</Text>
          <Text visual="smallRegular" color="text.mid">
            {t("WIDGETS_TAG_HINT")}
          </Text>

          <AudienceTagFilterSelect
            label={t("EVENT_ORGANIZERS_LABEL")}
            options={audienceTagOptions}
            selectedFilters={eventOrganizerAudienceFilters}
            onChange={setEventOrganizerAudienceFilters}
          />

          <AudienceTagFilterSelect
            label={t("EVENT_VENUES_LABEL")}
            options={audienceTagOptions}
            selectedFilters={eventVenueAudienceFilters}
            onChange={setEventVenueAudienceFilters}
          />

          <AudienceTagFilterSelect
            label={t("WHATS_NEARBY_LABEL")}
            options={audienceTagOptions}
            selectedFilters={whatsNearbyAudienceFilters}
            onChange={setWhatsNearbyAudienceFilters}
          />

          <Text color="text.light" visual="smallRegular">
            {t("WIDGETS_SETTINGS_NOTE")}{" "}
            <Link to="../widgets">{t("WIDGETS_SETTINGS_LINK")}</Link>
          </Text>
        </Flex>
      )}
    </Flex>
  );
};
